import { createSlice } from '@reduxjs/toolkit';
import i18n from 'i18next';
import { enqueueSnackbar } from 'notistack';
import { closeProjectResourcesView } from 'scenes/ProjectResources/store/actions/project-resources.actions';
import { arrayToMap } from 'utils/arrayToMap';
import { resetUserProfile } from './actions/user-profile-actions';
import { loadBusinessConfigurationsOfUser } from './thunks/loadBusinessConfigurationsOfUser';
import { loadUserData } from './thunks/loadUserData.thunk';
import { loadUserDataWithResources } from './thunks/loadUserDataWithResources.thunk';
import { recalculateUserSaldoThunk } from './thunks/recalculateUserSaldo.thunk';
import { setUserBusinessConfigurations } from './thunks/setUserBusinessConfigurations.thunk';

const resetState = {
  loading: false,
  user: {},
  userEmployerResources: {},
  userEmployerBusinessIds: [],
  userEmployeeConfigurations: [],
};

export const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState: {
    loading: false,
    user: {},
    userEmployerResources: {},
    userEmployerBusinessIds: [],
    userEmployeeConfigurations: [],
    recalculatingSaldo: false,
  },
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(loadUserData.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(loadUserData.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.userEmployerResources = action.payload.userEmployerResources;
        state.userEmployerBusinessIds = action.payload.userEmployerBusinessIds;
        state.userEmployeeConfigurations = arrayToMap(
          action.payload.userEmployeeConfigurations,
          'businessId'
        );

        state.loading = false;
      })
      .addCase(loadUserData.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(loadUserDataWithResources.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(loadUserDataWithResources.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.userEmployerResources = action.payload.userEmployerResources;
        state.userEmployerBusinessIds = action.payload.userEmployerBusinessIds;
        state.userEmployeeConfigurations = arrayToMap(
          action.payload.userEmployeeConfigurations,
          'businessId'
        );

        state.loading = false;
      })
      .addCase(loadUserDataWithResources.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(loadBusinessConfigurationsOfUser.fulfilled, (state, action) => {
        const flat = (action.payload || []).flat().filter(Boolean);

        flat.forEach((configuration) => {
          state.userEmployeeConfigurations[configuration.businessId] =
            configuration;
        });
      })
      .addCase(setUserBusinessConfigurations.fulfilled, (state, action) => {
        state.userEmployeeConfigurations[action.payload.businessId] =
          action.payload;
      })
      .addCase(closeProjectResourcesView, (state, action) => {
        state.user = {};
      })
      .addCase(resetUserProfile, (state, action) => {
        return {
          ...resetState,
        };
      })
      .addCase(recalculateUserSaldoThunk.pending, (state) => {
        state.recalculatingSaldo = true;
      })
      .addCase(recalculateUserSaldoThunk.fulfilled, (state) => {
        state.recalculatingSaldo = false;
        enqueueSnackbar(i18n.t('profile.saldo_recalculation_started'), {
          variant: 'success',
        });
      })
      .addCase(recalculateUserSaldoThunk.rejected, (state) => {
        state.recalculatingSaldo = false;
        enqueueSnackbar(i18n.t('profile.saldo_recalculation_failed'), {
          variant: 'error',
        });
      });
  },
});
